import { useDispatch } from 'react-redux';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { getCzMaxPriceKey } from '~/libs/infrastructure/api/users/czMaxPrice';
import {
	deleteProductGroupProducts,
	useProductGroupExportXls,
	useProductGroupExportEvidence,
} from '~/libs/infrastructure/api/users/productGroup';
import { Export } from '~/Api/User/ProductGroup/Export';
import { Product as GeneralProduct } from '~/Product';
import { handleError } from '~/ErrorHandler';
import { processExport } from '~/ProductGroupDownloadPopover/productGroupDownloadHelpers';
import type { ThunkDispatch } from '~/Store';

const isProductGroupLocked = (error: unknown) => {
	const codes: string[] = (error as any)?.response?.data?.errors?.map((e: { code: string }) => e.code) ?? [];
	return codes.includes('product-group-locked');
};

export function useUserProductGroup(userId: string | null, groupId: string) {
	const dispatch: ThunkDispatch<any> = useDispatch();
	const queryClient = useQueryClient();

	const exportXlsProductsMutation = useProductGroupExportXls(userId!, {
		onSuccess: (response) => dispatch(processExport(response.export as Export)),
		onError: (error: unknown) => dispatch(handleError(error)),
	});

	const exportEvidenceMutation = useProductGroupExportEvidence(userId!, {
		onSuccess: (response) => dispatch(processExport(response.export as Export)),
		onError: (error: unknown) => dispatch(handleError(error)),
	});

	const deleteProductsMutation = useMutation({
		mutationFn: (products: GeneralProduct[]) =>
			deleteProductGroupProducts(
				userId!,
				groupId,
				products.map((p) => p.id)
			),
		retry: (_, error) => isProductGroupLocked(error),
		retryDelay: 1000,
		onSuccess: () => queryClient.invalidateQueries({ queryKey: getCzMaxPriceKey(groupId) }),
		onError: (error: unknown) => dispatch(handleError(error)),
	});

	function exportProductsXls(groupId: string, currency: string) {
		exportXlsProductsMutation.mutate({ groupId, currency });
	}

	function exportEvidence(groupId: string) {
		exportEvidenceMutation.mutate({ groupId });
	}

	return {
		deleteProducts: deleteProductsMutation.mutateAsync,
		exportProductsXls,
		exportEvidence,
	};
}
